import { useState, useCallback } from "react";
import type { CardTransactionStatus } from "Infrastructure/Api/Api";
import { CardTransactionFiltersDrawer } from "Components/CardTransactions/CardTransactionFilters/CardTransactionFiltersDrawer";
import type { CardTransactionFilterManager } from "Components/CardTransactions/hooks/useCardTransactionFilterManager";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import type { FilterOption } from "Components/Shared/Filters/FilterOption";
import { FiltersTrigger } from "Components/Shared/Filters/FiltersTrigger";
import type { TimePeriod } from "Types/TimePeriod";

type Props = {
  filterManager: CardTransactionFilterManager;
};

export const CardTransactionFilter = ({
  filterManager: { filters, applyFilters, resetFilters, activeFilterCount },
}: Props) => {
  const [localFilters, setLocalFilters] =
    useState<CardTransactionFilters>(filters);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleTimePeriodChange = useCallback(
    (value: TimePeriod | undefined) => {
      setLocalFilters((prev) => ({
        ...prev,
        timePeriod: value,
        dateFrom: value === "custom" ? prev.dateFrom : undefined,
        dateTo: value === "custom" ? prev.dateTo : undefined,
      }));
    },
    [],
  );

  const handlePayPointChange = useCallback((option?: FilterOption) => {
    setLocalFilters((prev) => ({ ...prev, payPoint: option }));
  }, []);

  const handleEmployeeChange = useCallback((option?: FilterOption) => {
    setLocalFilters((prev) => ({ ...prev, employee: option }));
  }, []);

  const handleStatusChange = useCallback((value?: CardTransactionStatus) => {
    setLocalFilters((prev) => ({ ...prev, status: value }));
  }, []);

  const handleRefundOrCancellationChange = useCallback((value: boolean) => {
    // Unchecked must clear the filter entirely — sending `false` to the API is a sticky
    // "exclude refunds" filter the UI has no way to express or display.
    setLocalFilters((prev) => ({
      ...prev,
      isRefundOrCancellation: value ? true : undefined,
    }));
  }, []);

  const handleDateFromChange = useCallback((value?: Date) => {
    setLocalFilters((prev) => ({ ...prev, dateFrom: value }));
  }, []);

  const handleDateToChange = useCallback((value?: Date) => {
    setLocalFilters((prev) => ({ ...prev, dateTo: value }));
  }, []);

  const handleApply = useCallback(() => {
    applyFilters(localFilters);
    setIsDrawerOpen(false);
  }, [applyFilters, localFilters]);

  const handleReset = useCallback(() => {
    resetFilters();
    setIsDrawerOpen(false);
  }, [resetFilters]);

  return (
    <>
      <FiltersTrigger
        isOpen={isDrawerOpen}
        onClick={() => {
          setIsDrawerOpen(true);
          setLocalFilters(filters);
        }}
        activeFilterCount={activeFilterCount}
      />
      <CardTransactionFiltersDrawer
        isOpen={isDrawerOpen}
        localFilters={localFilters}
        onApply={handleApply}
        onReset={handleReset}
        onClose={() => setIsDrawerOpen(false)}
        onStatusChange={handleStatusChange}
        onRefundOrCancellationChange={handleRefundOrCancellationChange}
        onDateToChange={handleDateToChange}
        onPayPointChange={handlePayPointChange}
        onEmployeeChange={handleEmployeeChange}
        onDateFromChange={handleDateFromChange}
        onTimePeriodChange={handleTimePeriodChange}
      />
    </>
  );
};
